import { useState } from 'react';

/**
 * A profile photo, or a plain initial-letter placeholder when there is none on file (or the URL
 * fails to load). Shared by `AppShell.tsx`'s header and `SignaturePage.tsx`'s own photo panel so the
 * fallback behaviour cannot drift between the two — `Logo.tsx`'s `LogoLockup` is the precedent for
 * "an `<img>` with an `onError` fallback to something else drawn in-component," applied here to a
 * person's photo instead of a practice's logo.
 *
 * `photoUrl` is a short-lived presigned URL (`server/src/documents/storage.ts`'s `getDocumentUrl`) —
 * this component never caches it past the render that received it; a caller that wants a fresh one
 * later re-fetches through its own query, it does not ask this component to refresh itself.
 */
export function ProfilePhotoAvatar({
  photoUrl,
  label,
  size = 32,
  className,
}: {
  readonly photoUrl: string | null;
  /** The person this photo belongs to — "Jane Smith" or an email. Used for the initial-letter
   * fallback and as the accessible name in both states. */
  readonly label: string;
  readonly size?: number;
  readonly className?: string;
}) {
  // Tracks a load failure for the *specific* URL currently passed in, matching `LogoLockup`'s own
  // reasoning: a stale "broken" flag left over from a previous, different `photoUrl` (a re-upload
  // producing a new presigned URL) must not keep this component stuck on the fallback forever.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const trimmed = label.trim();
  const initial = trimmed.length > 0 ? trimmed.charAt(0).toUpperCase() : '?';

  if (photoUrl !== null && photoUrl !== failedUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        aria-label={label}
        className={['rounded-full object-cover', className].filter(Boolean).join(' ')}
        style={{ height: size, width: size, flexShrink: 0 }}
        onError={() => {
          setFailedUrl(photoUrl);
        }}
      />
    );
  }

  return (
    // Theme tokens (`bg-raised`/`text-ink-muted`/`border-line`), not a hardcoded hex — this
    // placeholder reacts to light/dark mode and a user's accent pick the same as the rest of the
    // app's chrome, matching every other themed surface in this codebase rather than inventing a
    // one-off colour just for this component.
    <span
      role="img"
      aria-label={label}
      className={[
        'inline-flex shrink-0 items-center justify-center rounded-full border border-line bg-raised font-semibold text-ink-muted',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ height: size, width: size, fontSize: Math.round(size * 0.45) }}
    >
      {initial}
    </span>
  );
}
